import React, { useEffect, useMemo, useState } from "react";
import Drawer from "@mui/material/Drawer";
import { Checkbox } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BaseButton from "../BaseButton";

const TableColumnSettings = ({
  open,
  onClose,
  title = "Customize Columns",
  columns = [],
  visibility = {},
  lockedFields = [],
  onApply,
  onReset,
}) => {
  const lockedColumns = useMemo(
    () => columns.filter((c) => lockedFields.includes(c.field)),
    [columns, lockedFields],
  );

  const [items, setItems] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);

  useEffect(() => {
    if (!open) return;
    const next = columns
      .filter((c) => !lockedFields.includes(c.field))
      .map((c) => ({
        field: c.field,
        headerName: c.headerName || c.field,
        visible: visibility[c.field] !== false,
      }));
    setItems(next);
    setDragIndex(null);
  }, [open]);

  const visibleCount = lockedColumns.length + items.filter((i) => i.visible).length;

  const move = (from, to) => {
    if (to < 0 || to >= items.length || from === to) return;
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const toggleVisibility = (index) => {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, visible: !item.visible } : item)),
    );
  };

  const setAll = (visible) => {
    setItems((prev) => prev.map((item) => ({ ...item, visible })));
  };

  const handleDrop = (index) => {
    if (dragIndex === null) return;
    move(dragIndex, index);
    setDragIndex(null);
  };

  const handleApply = () => {
    const order = [...lockedColumns.map((c) => c.field), ...items.map((i) => i.field)];
    const nextVisibility = {};
    items.forEach((i) => {
      nextVisibility[i.field] = i.visible;
    });
    lockedColumns.forEach((c) => {
      nextVisibility[c.field] = true;
    });
    onApply?.(order, nextVisibility);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "420px",
          maxWidth: "100vw",
          borderTopLeftRadius: 6,
          borderBottomLeftRadius: 6,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <div className="relative flex-shrink-0 border-b p-4">
        <button
          onClick={onClose}
          className="absolute top-2 right-0 m-2 hover:text-black text-gray-600 cursor-pointer"
          aria-label="Close"
        >
          <CloseIcon className="text-2xl" />
        </button>
        <h2 className="text-lg font-semibold pr-10 text-[#686868]">{title}</h2>
        <p className="text-xs text-gray-500 mt-1 pr-10">
          Show or hide columns and drag to reorder them.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-gray-500">
            {visibleCount} column{visibleCount === 1 ? "" : "s"} visible
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setAll(true)}
              className="text-xs text-primary hover:underline"
            >
              Show all
            </button>
            <button
              type="button"
              onClick={() => setAll(false)}
              className="text-xs text-primary hover:underline"
            >
              Hide all
            </button>
          </div>
        </div>

        <ul className="flex flex-col gap-1">
          {/* Locked columns – always visible, always first, not re-orderable */}
          {lockedColumns.map((c) => (
            <li
              key={c.field}
              className="flex items-center gap-2 rounded-md border border-gray-100 bg-gray-50 px-2 py-1.5"
            >
              <i className="fa-solid fa-lock text-gray-300 text-xs w-4 text-center" />
              <Checkbox checked disabled size="small" sx={{ p: 0.5 }} />
              <span className="flex-1 text-sm text-gray-600 truncate">
                {c.headerName || c.field}
              </span>
              <span className="text-[10px] uppercase tracking-wide text-gray-400">Pinned</span>
            </li>
          ))}

          {/* Re-orderable columns */}
          {items.map((item, index) => (
            <li
              key={item.field}
              draggable
              onDragStart={() => setDragIndex(index)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(index)}
              onDragEnd={() => setDragIndex(null)}
              className={`flex items-center gap-2 rounded-md border px-2 py-1.5 bg-white cursor-grab active:cursor-grabbing ${
                dragIndex === index
                  ? "border-primary shadow-sm"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <i className="fa-solid fa-grip-vertical text-gray-300 text-xs w-4 text-center" />
              <Checkbox
                checked={item.visible}
                onChange={() => toggleVisibility(index)}
                size="small"
                sx={{ p: 0.5 }}
              />
              <span
                className={`flex-1 text-sm truncate ${
                  item.visible ? "text-gray-700" : "text-gray-400"
                }`}
              >
                {item.headerName}
              </span>
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => move(index, index - 1)}
                  disabled={index === 0}
                  aria-label="Move up"
                  className="p-1 text-gray-400 hover:text-primary disabled:opacity-30 disabled:hover:text-gray-400"
                >
                  <i className="fa-solid fa-chevron-up text-xs" />
                </button>
                <button
                  type="button"
                  onClick={() => move(index, index + 1)}
                  disabled={index === items.length - 1}
                  aria-label="Move down"
                  className="p-1 text-gray-400 hover:text-primary disabled:opacity-30 disabled:hover:text-gray-400"
                >
                  <i className="fa-solid fa-chevron-down text-xs" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div
        className="flex-shrink-0 flex items-center justify-center bg-white py-4 gap-3"
        style={{ boxShadow: "5px 0px 14px 5px rgba(0, 0, 0, 0.07)" }}
      >
        <BaseButton
          className="py-[5px] px-4"
          style={{
            border: "2px solid #0F40AE",
            color: "#0F40AE",
            backgroundColor: "white",
          }}
          fullWidth={false}
          onClick={onReset}
        >
          Reset to Default
        </BaseButton>
        <BaseButton
          className="py-[5px] px-4 bg-[#E8E8E8] text-dark"
          fullWidth={false}
          onClick={onClose}
        >
          <p>Cancel</p>
        </BaseButton>
        <BaseButton
          className="py-[5px] px-4"
          color="primary"
          fullWidth={false}
          onClick={handleApply}
        >
          Apply
        </BaseButton>
      </div>
    </Drawer>
  );
};

export default TableColumnSettings;
